// Lightweight, asset-free PixiJS widgets for the Dice UI: a glass pill button, a
// small icon button, a labelled stat card, and a number-selector cell. All are
// drawn with Graphics + Text so they are crisp at any DPI with no remote assets.
// They are pure renderers; click handling calls back into the controller.
import { Container, Graphics, Text, TextStyle, type TextStyleOptions } from 'pixi.js';
import {
  primaryValueFontPx,
  statCardValueBand,
} from './stat-card-layout.js';

// System font stack covering Latin, Cyrillic, Greek, Arabic, Hebrew, CJK, Thai,
// Devanagari, and Baltic diacritics — no proprietary font files bundled.
const FONT: TextStyleOptions = {
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, ' +
    '"Noto Sans", "Noto Sans Arabic", "Noto Sans Hebrew", "Noto Sans Devanagari", ' +
    '"Noto Sans Thai", "Noto Sans SC", "Noto Sans TC", "Noto Sans JP", "Noto Sans KR", ' +
    'sans-serif',
  fill: 0xdce8ff,
  align: 'center',
};

const NEON = 0x6ee2ff;
const NEON_SOFT = 0x33518f;

export function label(text: string, size: number, opts: Partial<TextStyleOptions> = {}): Text {
  const t = new Text({ text, style: new TextStyle({ ...FONT, fontSize: size, ...opts }) });
  t.anchor.set(0.5);
  t.resolution = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
  return t;
}

interface ButtonStyle {
  fill: number;
  fillAlpha: number;
  stroke: number;
  textColor: number;
}

const PRIMARY: ButtonStyle = { fill: 0x1f6dff, fillAlpha: 1, stroke: NEON, textColor: 0xffffff };
const GLASS: ButtonStyle = { fill: 0x142450, fillAlpha: 0.55, stroke: NEON_SOFT, textColor: 0xdce8ff };
// Clearly-disabled look: muted fill, NO neon glow, low-contrast (but still legible)
// text. Paired with a not-allowed cursor so the button reads as inactive.
const DISABLED: ButtonStyle = { fill: 0x0d1530, fillAlpha: 0.5, stroke: 0x27365c, textColor: 0x6f81ac };

export class PillButton extends Container {
  private readonly bg = new Graphics();
  private readonly text: Text;
  private w: number;
  private h: number;
  private enabled = true;
  private readonly style: ButtonStyle;
  private onActivate: (() => void) | undefined;

  constructor(text: string, width: number, height: number, primary = false) {
    super();
    this.w = width;
    this.h = height;
    this.style = primary ? PRIMARY : GLASS;
    this.addChild(this.bg);
    this.text = label(text, Math.round(height * 0.36), { fill: this.style.textColor, fontWeight: 'bold' });
    this.addChild(this.text);
    this.draw();
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', () => {
      if (this.enabled) this.onActivate?.();
    });
  }

  setText(text: string): void {
    this.text.text = text;
  }

  onClick(cb: () => void): this {
    this.onActivate = cb;
    return this;
  }

  resize(width: number, height: number): void {
    this.w = width;
    this.h = height;
    this.text.style.fontSize = Math.round(height * 0.36);
    this.draw();
  }

  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    // not-allowed makes the disabled state unmistakable on desktop pointers.
    this.cursor = enabled ? 'pointer' : 'not-allowed';
    this.alpha = enabled ? 1 : 0.6;
    this.text.style.fill = (enabled ? this.style : DISABLED).textColor;
    this.draw();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private draw(): void {
    const g = this.bg;
    const s = this.enabled ? this.style : DISABLED;
    g.clear();
    const r = Math.min(this.h / 2, 22);
    g.roundRect(0, 0, this.w, this.h, r).fill({ color: s.fill, alpha: s.fillAlpha });
    g.roundRect(0, 0, this.w, this.h, r).stroke({ width: 2, color: s.stroke });
    this.text.position.set(this.w / 2, this.h / 2);
  }
}

export type ToolIcon = 'history' | 'rules' | 'sound-on' | 'sound-off';

/**
 * A premium utility control for the top toolbar (History / Rules / Sound). It is
 * a square glass button with a crisp, asset-free VECTOR icon and explicit hover,
 * pressed, active (toggle) and disabled states. The visible icon swaps for the
 * sound toggle so its ON/OFF state reads at a glance; the DOM mirror carries the
 * accessible label + aria-pressed for keyboard/AT users.
 */
export class ToolButton extends Container {
  private readonly bg = new Graphics();
  private readonly icon = new Graphics();
  private size: number;
  private kind: ToolIcon;
  private hovered = false;
  private pressed = false;
  /** Persistent toggled-on emphasis (used to mark the sound button ON). */
  private active = false;
  private enabled = true;
  private onActivate: (() => void) | undefined;

  constructor(kind: ToolIcon, size: number) {
    super();
    this.kind = kind;
    this.size = size;
    this.addChild(this.bg, this.icon);
    this.draw();
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', () => {
      if (this.enabled) this.onActivate?.();
    });
    this.on('pointerover', () => this.setHover(true));
    this.on('pointerout', () => {
      this.setHover(false);
      this.setPressed(false);
    });
    this.on('pointerdown', () => this.setPressed(true));
    this.on('pointerup', () => this.setPressed(false));
    this.on('pointerupoutside', () => this.setPressed(false));
  }

  onClick(cb: () => void): this {
    this.onActivate = cb;
    return this;
  }

  setIcon(kind: ToolIcon): void {
    if (kind === this.kind) return;
    this.kind = kind;
    this.draw();
  }

  /** Persistent toggled-on emphasis (the sound button uses this for ON). */
  setActive(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    this.draw();
  }

  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    this.cursor = enabled ? 'pointer' : 'not-allowed';
    if (!enabled) {
      this.hovered = false;
      this.pressed = false;
    }
    this.alpha = enabled ? 1 : 0.5;
    this.draw();
  }

  resize(size: number): void {
    this.size = size;
    this.draw();
  }

  private setHover(h: boolean): void {
    if (!this.enabled || h === this.hovered) return;
    this.hovered = h;
    this.draw();
  }

  private setPressed(p: boolean): void {
    const next = p && this.enabled;
    if (next === this.pressed) return;
    this.pressed = next;
    this.draw();
  }

  private draw(): void {
    const g = this.bg;
    g.clear();
    const s = this.size;
    const r = Math.min(s * 0.3, 18);
    // Surface: brighter when active (sound ON) or hovered; deeper on press.
    const fill = this.active ? 0x1f6dff : this.pressed ? 0x0d1c44 : this.hovered ? 0x1a2f63 : 0x142450;
    const fillAlpha = this.active ? 0.92 : this.hovered ? 0.85 : 0.62;
    const stroke = this.active ? NEON : this.hovered ? NEON : NEON_SOFT;
    g.roundRect(0, 0, s, s, r).fill({ color: fill, alpha: fillAlpha });
    g.roundRect(0, 0, s, s, r).stroke({ width: this.hovered || this.active ? 2.5 : 2, color: stroke });

    const iconColor = !this.enabled ? 0x6f81ac : this.active ? 0xffffff : this.hovered ? 0xeaf3ff : 0xcfe0ff;
    this.icon.position.set(s / 2, s / 2);
    drawToolIcon(this.icon, this.kind, s * 0.46, iconColor);
  }
}

/** Draws one crisp vector icon centred on the Graphics' local origin (0,0). */
function drawToolIcon(g: Graphics, kind: ToolIcon, box: number, color: number): void {
  g.clear();
  const w = Math.max(2, box * 0.12); // stroke weight scaled to the icon box
  const u = box / 2; // half-extent of the icon box
  if (kind === 'history') {
    // Clock face with an anticlockwise "recall" tick and two hands.
    g.circle(0, 0, u).stroke({ width: w, color });
    g.moveTo(0, 0).lineTo(0, -u * 0.55).stroke({ width: w, color, cap: 'round' });
    g.moveTo(0, 0).lineTo(u * 0.42, u * 0.12).stroke({ width: w, color, cap: 'round' });
  } else if (kind === 'rules') {
    // A small "rules sheet": three rounded list lines of varying length.
    const lines = [
      [-u, -u * 0.5, u, -u * 0.5],
      [-u, 0, u * 0.55, 0],
      [-u, u * 0.5, u, u * 0.5],
    ];
    for (const [x1, y1, x2, y2] of lines) {
      g.moveTo(x1!, y1!).lineTo(x2!, y2!).stroke({ width: w, color, cap: 'round' });
    }
  } else {
    // Speaker body + cone, then either sound waves (on) or a mute cross (off).
    const bodyX = -u * 0.85;
    g.rect(bodyX, -u * 0.28, u * 0.45, u * 0.56).fill({ color });
    g.poly([bodyX + u * 0.45, -u * 0.28, -u * 0.05, -u * 0.62, -u * 0.05, u * 0.62, bodyX + u * 0.45, u * 0.28]).fill({
      color,
    });
    if (kind === 'sound-on') {
      g.arc(-u * 0.05, 0, u * 0.5, -Math.PI / 3, Math.PI / 3).stroke({ width: w, color, cap: 'round' });
      g.arc(-u * 0.05, 0, u * 0.85, -Math.PI / 3, Math.PI / 3).stroke({ width: w, color, cap: 'round' });
    } else {
      const cx = u * 0.45;
      g.moveTo(cx - u * 0.28, -u * 0.28).lineTo(cx + u * 0.28, u * 0.28).stroke({ width: w, color, cap: 'round' });
      g.moveTo(cx + u * 0.28, -u * 0.28).lineTo(cx - u * 0.28, u * 0.28).stroke({ width: w, color, cap: 'round' });
    }
  }
}

export type StatCardVariant = 'primary' | 'secondary' | 'default';

const STAT_FONTS = {
  primary: { caption: { ratio: 0.2, min: 11, max: 14 }, value: { ratio: 0.42, min: 18, max: 30 } },
  secondary: { caption: { ratio: 0.24, min: 11, max: 14 }, value: { ratio: 0.38, min: 16, max: 24 } },
  default: { caption: { ratio: 0.2, min: 10, max: 14 }, value: { ratio: 0.34, min: 14, max: 22 } },
} as const;

function clampFont(size: number, min: number, max: number): number {
  return Math.round(Math.max(min, Math.min(max, size)));
}

export class StatCard extends Container {
  private readonly bg = new Graphics();
  private readonly caption: Text;
  private readonly value: Text;
  private w: number;
  private h: number;
  private variant: StatCardVariant = 'default';
  private valueFontPx = 14;

  constructor(caption: string, width: number, height: number, variant: StatCardVariant = 'default') {
    super();
    this.w = width;
    this.h = height;
    this.variant = variant;
    this.addChild(this.bg);
    this.caption = label(caption, 12, { fill: 0x9fb6e0, letterSpacing: 0.5 });
    this.caption.anchor.set(0.5, 0);
    this.value = label('—', 14, { fill: 0xffffff, fontWeight: 'bold', letterSpacing: 0, lineHeight: 1.05 });
    this.value.anchor.set(0.5, 0.5);
    this.addChild(this.caption, this.value);
    this.applyFonts();
    this.draw();
  }

  setCaption(text: string): void {
    this.caption.text = text;
    this.fitValue();
  }

  setValue(text: string): void {
    this.value.text = text;
    this.fitValue();
  }

  /** Current value font size in CSS pixels (for tests). */
  valueFontSize(): number {
    return this.valueFontPx * this.value.scale.x;
  }

  resize(width: number, height: number, variant?: StatCardVariant): void {
    this.w = width;
    this.h = height;
    if (variant) this.variant = variant;
    this.applyFonts();
    this.draw();
    this.fitValue();
  }

  private applyFonts(): void {
    const spec = STAT_FONTS[this.variant];
    this.caption.style.fontSize = clampFont(this.h * spec.caption.ratio, spec.caption.min, spec.caption.max);
    if (this.variant === 'primary') {
      this.valueFontPx = primaryValueFontPx(this.w, this.h);
    } else {
      this.valueFontPx = clampFont(this.h * spec.value.ratio, spec.value.min, spec.value.max);
    }
    this.value.style.fontSize = this.valueFontPx;
  }

  /** Vertical band below the caption reserved for the value (px). */
  private valueBand(): { top: number; bottom: number; height: number; centerY: number } {
    const captionFontPx = this.caption.style.fontSize as number;
    return statCardValueBand(this.h, captionFontPx);
  }

  /**
   * Shrinks the value font (then scale as last resort) so long amounts stay inside
   * the card without clipping, wrapping, or ellipsis.
   */
  private fitValue(): void {
    const spec = STAT_FONTS[this.variant];
    const padX = Math.max(4, this.w * 0.06);
    const maxW = Math.max(8, this.w - padX * 2);
    const band = this.valueBand();
    const minSize = this.variant === 'primary'
      ? Math.max(11, Math.floor(band.height / 1.08))
      : spec.value.min;

    let size = this.valueFontPx;
    this.value.scale.set(1);
    while (size >= minSize) {
      this.value.style.fontSize = size;
      if (this.value.width <= maxW && this.value.height <= band.height + 0.5) {
        this.valueFontPx = size;
        return;
      }
      size -= 1;
    }
    this.value.style.fontSize = minSize;
    this.valueFontPx = minSize;
    if (this.value.width > maxW) {
      this.value.scale.set(maxW / this.value.width);
    }
  }

  private draw(): void {
    const g = this.bg;
    g.clear();
    const r = Math.min(this.h * 0.24, 18);
    g.roundRect(0, 0, this.w, this.h, r).fill({ color: 0x0c1a40, alpha: 0.7 });
    g.roundRect(0, 0, this.w, this.h, r).stroke({ width: 1.5, color: NEON_SOFT });
    const padTop = Math.max(2, this.h * 0.1);
    this.caption.position.set(this.w / 2, padTop);
    const band = this.valueBand();
    this.value.position.set(this.w / 2, band.centerY);
  }

  /** Layout metrics for unit tests (value center and bounds). */
  layoutMetrics(): {
    valueCenterY: number;
    valueHeight: number;
    cardHeight: number;
    valueBottom: number;
    valueTop: number;
  } {
    const band = this.valueBand();
    const vh = this.value.height * this.value.scale.y;
    const cy = band.centerY;
    return {
      valueCenterY: cy,
      valueHeight: vh,
      cardHeight: this.h,
      valueTop: cy - vh / 2,
      valueBottom: cy + vh / 2,
    };
  }
}

export class NumberCell extends Container {
  private readonly bg = new Graphics();
  private readonly text: Text;
  private size: number;
  readonly value: number;
  private selected = false;
  private enabled = true;
  private hovered = false;
  private pressed = false;
  private onActivate: ((n: number) => void) | undefined;

  constructor(value: number, size: number) {
    super();
    this.value = value;
    this.size = size;
    this.addChild(this.bg);
    this.text = label(String(value), Math.round(size * 0.5), { fontWeight: 'bold' });
    this.addChild(this.text);
    this.draw();
    this.eventMode = 'static';
    this.cursor = 'pointer';
    // The whole tile is the hit area (set in draw()), so taps anywhere select it.
    this.on('pointertap', () => {
      if (this.enabled) this.onActivate?.(this.value);
    });
    // Distinct hover + pressed feedback. All states are pure repaints — no scale
    // or size change — so selecting/pressing never shifts the grid layout.
    this.on('pointerover', () => this.setHover(true));
    this.on('pointerout', () => {
      this.setHover(false);
      this.setPressed(false);
    });
    this.on('pointerdown', () => this.setPressed(true));
    this.on('pointerup', () => this.setPressed(false));
    this.on('pointerupoutside', () => this.setPressed(false));
  }

  onSelect(cb: (n: number) => void): this {
    this.onActivate = cb;
    return this;
  }

  setSelected(selected: boolean): void {
    if (selected === this.selected) return;
    this.selected = selected;
    this.draw();
  }

  isSelected(): boolean {
    return this.selected;
  }

  private setHover(h: boolean): void {
    if (!this.enabled || h === this.hovered) return;
    this.hovered = h;
    this.draw();
  }

  private setPressed(p: boolean): void {
    if (p === this.pressed) return;
    this.pressed = p && this.enabled;
    this.draw();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    this.cursor = enabled ? 'pointer' : 'not-allowed';
    if (!enabled) {
      this.hovered = false;
      this.pressed = false;
    }
    this.alpha = enabled ? 1 : 0.5;
    this.draw();
  }

  resize(size: number): void {
    this.size = size;
    this.text.style.fontSize = Math.round(size * 0.5);
    this.draw();
  }

  private draw(): void {
    const g = this.bg;
    g.clear();
    const r = this.size * 0.24;
    if (this.selected) {
      // Selected: bright blue surface, neon border + soft outer glow, white text.
      g.roundRect(-4, -4, this.size + 8, this.size + 8, r + 2).fill({ color: 0x38e8a0, alpha: 0.28 });
      g.roundRect(0, 0, this.size, this.size, r).fill({ color: this.pressed ? 0x1857d6 : 0x1f6dff, alpha: 1 });
      g.roundRect(0, 0, this.size, this.size, r).stroke({ width: 3, color: 0x7dffc0 });
      this.text.style.fill = 0xffffff;
    } else {
      // Default / hover / pressed: translucent surface; hover lifts the contrast
      // and lights the border neon to signal interactivity.
      const fillAlpha = this.pressed ? 0.92 : this.hovered ? 0.85 : 0.7;
      const fillColor = this.pressed ? 0x16294f : this.hovered ? 0x163058 : 0x102046;
      g.roundRect(0, 0, this.size, this.size, r).fill({ color: fillColor, alpha: fillAlpha });
      g.roundRect(0, 0, this.size, this.size, r).stroke({
        width: this.hovered ? 2.5 : 1.5,
        color: this.hovered ? NEON : NEON_SOFT,
      });
      this.text.style.fill = this.hovered ? 0xeaf3ff : 0xb9c9ee;
    }
    this.text.position.set(this.size / 2, this.size / 2);
  }
}
